import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { CheckboxGroupProps, CheckboxOption } from './types';
import { styles } from './styles';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';

function getEnabledOptions(options: CheckboxOption[]) {
  return options.filter(opt => !opt.disabled);
}

function computeParentFlags(options: CheckboxOption[]) {
  const enabled = getEnabledOptions(options);
  if (enabled.length === 0) {
    return { allChecked: false, someChecked: false, anyChecked: false };
  }
  const checkedCount = enabled.filter(o => o.checked).length;
  return {
    allChecked: checkedCount === enabled.length,
    someChecked: checkedCount > 0 && checkedCount < enabled.length,
    anyChecked: checkedCount > 0,
  };
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  label,
  options,
  onChange,
  disabled = false,
  className,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const canCollapse = options.length > 0;

  const { allChecked, someChecked } = useMemo(() => computeParentFlags(options), [options]);

  const enabledCount = useMemo(() => getEnabledOptions(options).length, [options]);
  const parentDisabled = disabled || enabledCount === 0;

  const emitChange = (nextOptions: CheckboxOption[]) => {
    const { anyChecked: nextAny } = computeParentFlags(nextOptions);
    onChange({ parentChecked: nextAny, options: nextOptions });
  };

  const handleParentChange = (checked: boolean) => {
    const nextOptions = options.map(opt => (opt.disabled ? opt : { ...opt, checked }));
    emitChange(nextOptions);
  };

  const handleChildChange = (id: string, checked: boolean) => {
    const nextOptions = options.map(opt => (opt.id === id ? { ...opt, checked } : opt));
    emitChange(nextOptions);
  };

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.parentRow}>
        <div className={styles.parentCheckboxWrapper}>
          <Checkbox
            label={label}
            checked={allChecked}
            indeterminate={someChecked}
            onChange={handleParentChange}
            disabled={parentDisabled}
          />
        </div>
        {canCollapse && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={styles.collapseTrigger}
            onClick={() => setExpanded(prev => !prev)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse sub-options' : 'Expand sub-options'}
            icon={
              <FontAwesomeIcon
                icon={expanded ? faChevronDown : faChevronRight}
                className={styles.collapseIcon}
              />
            }
          />
        )}
      </div>
      {canCollapse && expanded && (
        <div className={styles.childrenContainer}>
          {options.map(option => (
            <Checkbox
              key={option.id}
              id={option.id}
              label={option.label}
              checked={option.checked}
              onChange={checked => handleChildChange(option.id, checked)}
              disabled={disabled || option.disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export type { CheckboxGroupProps, CheckboxOption, CheckboxGroupChangePayload } from './types';
export default CheckboxGroup;
